import { useQuery } from "@apollo/client/react"
import { Divider } from "@mui/material"
import Paper from "@mui/material/Paper"
import React, { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router"
import { useNavigate, useSearchParams } from "react-router-dom"

import ClientModal from "@/components/client_modal"
import Pagination from "@/components/pagination"
import SearchInput from "@/components/search_input"
import { useVenue } from "@/contexts/venue"
import {
  GetCustomerContactsDocument,
  type GetCustomerContactsQuery,
  type GetCustomerContactsQueryVariables,
  CustomerContactSort,
  SortDirection,
} from "@/graphql/graphql"
import i18n from "@/i18n"
import { usePanelLink, usePreviousUntilDefined } from "@/shared/hooks"

import Layout from "../../../components/layout"

import ClientsTable from "./clients_table"

const STYLES = {
  pageTitle: "text-3xl mt-3 mb-0 font-medium",
  filtersBar: "p-4 pl-5 flex justify-between items-center",
  paginationBottom: "flex justify-center mt-4",
} as const

const ClientsPage = () => {
  const { venue } = useVenue()
  const navigate = useNavigate()
  const linkTo = usePanelLink()
  const [searchParams, setSearchParams] = useSearchParams()
  const hasValidPageNumber = searchParams.get("page") !== null && !isNaN(Number(searchParams.get("page")))
  const page = hasValidPageNumber ? Number(searchParams.get("page")) : 1
  const perPage = Number(searchParams.get("per_page")) || 10
  const search = searchParams.get("query") || null
  const sortBy = (searchParams.get("sort_by") as CustomerContactSort) || null
  const sortDirection = (searchParams.get("sort_direction") as SortDirection) || SortDirection.Asc
  const [query, setQuery] = useState("")

  const { customerContactId } = useParams()

  useEffect(() => {
    if (!venue.permissions?.viewClients) {
      navigate(linkTo("settings"))
    }
  }, [venue.permissions?.viewClients, navigate, linkTo])

  const onSearch = useCallback(
    (query: string) => {
      setSearchParams({ query, page: "1" })
    },
    [setSearchParams],
  )

  const { data, loading } = useQuery<GetCustomerContactsQuery, GetCustomerContactsQueryVariables>(
    GetCustomerContactsDocument,
    {
      variables: { page, perPage, search, sort: sortBy, sortDirection },
    },
  )

  useEffect(() => {
    if (search !== query) {
      setQuery(search)
    }
  }, [search])

  const createQueryParams = (
    overrides: {
      query?: string
      per_page?: string | number
      page?: string | number
      sort_by?: CustomerContactSort | null
      sort_direction?: SortDirection
    } = {},
  ) => {
    const query = overrides.query || searchParams.get("query")
    const perPage = overrides.per_page || searchParams.get("per_page")
    const newPage = overrides.page || page
    const newSortBy = overrides.sort_by !== undefined ? overrides.sort_by : searchParams.get("sort_by")
    const newSortDirection = overrides.sort_direction || searchParams.get("sort_direction")

    return `?page=${newPage}${perPage ? `&per_page=${perPage}` : ""}${query ? `&query=${query}` : ""}${newSortBy ? `&sort_by=${newSortBy}` : ""}${newSortDirection ? `&sort_direction=${newSortDirection}` : ""}`
  }

  const onPageChange = useCallback(
    (page: number) => {
      navigate(linkTo(`clients${createQueryParams({ page })}`))
    },
    [linkTo],
  )

  const onPerPageChange = useCallback(
    (perPage: number) => {
      navigate(linkTo(`clients${createQueryParams({ page: 1, per_page: perPage })}`))
    },
    [linkTo],
  )

  const onCustomerContactOpen = useCallback(
    (contact: GetCustomerContactsQuery["business"]["customerContacts"]["collection"][0]) => {
      navigate(linkTo(`clients/${contact.id}${createQueryParams()}`))
    },
    [linkTo],
  )

  const onCustomerContactClose = useCallback(() => {
    navigate(linkTo(`clients${createQueryParams()}`))
  }, [linkTo])

  const onSort = useCallback(
    (column: CustomerContactSort) => {
      let newSortDirection = SortDirection.Asc

      // If clicking the same column, toggle direction
      if (sortBy === column) {
        newSortDirection = sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc
      }

      navigate(linkTo(`clients${createQueryParams({ page: 1, sort_by: column, sort_direction: newSortDirection })}`))
    },
    [linkTo, sortBy, sortDirection, createQueryParams, navigate],
  )

  const metadata = usePreviousUntilDefined(data?.business?.customerContacts?.metadata)

  const contacts = data?.business?.customerContacts?.collection

  return (
    <Layout title={i18n.t("app.clients.header")}>
      <div data-testid="panel-clients-page">
        <h1 className={STYLES.pageTitle}>{i18n.t("app.clients.header")}</h1>

        <Paper className="mt-4">
          <div className={STYLES.filtersBar}>
            <SearchInput
              data-testid="panel-clients-search"
              query={query}
              setQuery={setQuery}
              onSearch={onSearch}
              initialValue={search}
              size="small"
              placeholder={i18n.t("app.clients.search_placeholder")}
            />

            {metadata ? (
              <Pagination
                data-testid="panel-clients-pagination-top"
                metadata={metadata}
                page={page}
                onPageChange={onPageChange}
                perPage={perPage}
                onPerPageChange={onPerPageChange}
              />
            ) : null}
          </div>

          <Divider />
          <ClientsTable
            loading={loading}
            contacts={contacts}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
            onContactOpen={onCustomerContactOpen}
          />
        </Paper>

        {metadata ? (
          <div className={STYLES.paginationBottom}>
            <Pagination
              data-testid="panel-clients-pagination-bottom"
              metadata={metadata}
              page={page}
              onPageChange={onPageChange}
              variant="outlined"
              shape="rounded"
            />
          </div>
        ) : null}

        <ClientModal customerContactId={customerContactId} onClose={onCustomerContactClose} />
      </div>
    </Layout>
  )
}

export default ClientsPage
